import { buildPublicApiUrl } from './official-promotion'

/**
 * 官网首屏「下载信息」里的动态部分：当前安装包的版本号、大小、更新时间。
 *
 * 数据来自官网后端 `GET /api/v1/public/pkg/info`：与 `/pkg/download` 同一判据选中的那个包，
 * 只读、不计下载数、不含任何下载地址。三个字段各自可缺省（库里没有就不返回），
 * 这里也只展示拿到的那几项，**绝不补默认值**——宁可少一项，也不能写一个和实际下载不符的数。
 *
 * 失败、超时、返回格式不对时一律静默：页面保持原有的静态信息，不报错、不出占位、不闪烁。
 *
 * 【老内核】本文件只用 ES2015 语法（产物 target es2015，plugin-legacy 再降一档），
 * 不依赖 AbortController / Intl 时区：前者老内核没有，后者老内核的时区数据不可靠。
 */
export interface DownloadInfo {
  /** 版本号，如 1.1.38 */
  version: string
  /** 人类可读大小，如 102.2 MB */
  size: string
  /** 更新日期（北京时间），如 2026-10-28 */
  date: string
}

export const DownloadInfoUrl = buildPublicApiUrl('/public/pkg/info')

/** 超过这个时间还没回来就放弃：首屏不能为一行附加信息一直挂着请求。 */
const RequestTimeoutMs = 4000

/** 版本号只认常见字符，长度封顶：防止异常数据把一行信息撑破版式。 */
const VersionPattern = /^[0-9A-Za-z][0-9A-Za-z._+-]{0,31}$/

/** 安装包大小合理范围：1 KB ~ 4 GB，越界视为脏数据不展示。 */
const MinSizeBytes = 1024
const MaxSizeBytes = 4 * 1024 * 1024 * 1024

/** 时间戳合理范围（秒）：2020-01-01 起，且不晚于「现在 + 1 天」。 */
const MinUnixSeconds = 1577836800

const ShanghaiOffsetMs = 8 * 60 * 60 * 1000

/** 字节数 → 「102.2 MB」。按 1024 进制（与安卓系统、各应用商店显示一致）。 */
export function formatPackageSize(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** 秒级时间戳 → 北京时间日期「2026-10-28」。不依赖浏览器时区设置。 */
export function formatShanghaiDate(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000 + ShanghaiOffsetMs)
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value && value > 0
}

/** 把接口 data 变成可展示的信息；任何一项不合法就单独丢掉那一项。全都没有返回 null。 */
export function toDownloadInfo(data: unknown, nowMs: number = Date.now()): DownloadInfo | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as { versionName?: unknown; sizeBytes?: unknown; updatedAt?: unknown }
  const info: DownloadInfo = { version: '', size: '', date: '' }

  if (typeof raw.versionName === 'string') {
    const version = raw.versionName.trim()
    if (VersionPattern.test(version)) info.version = version
  }
  if (isPositiveInteger(raw.sizeBytes) && raw.sizeBytes >= MinSizeBytes && raw.sizeBytes <= MaxSizeBytes) {
    info.size = formatPackageSize(raw.sizeBytes)
  }
  if (isPositiveInteger(raw.updatedAt) && raw.updatedAt >= MinUnixSeconds && raw.updatedAt * 1000 <= nowMs + 86400000) {
    info.date = formatShanghaiDate(raw.updatedAt)
  }
  return info.version || info.size || info.date ? info : null
}

/**
 * 请求一次下载信息。永不抛错：失败、超时、非 200、业务码非 200、字段全缺都返回 null。
 */
export function fetchDownloadInfo(timeoutMs: number = RequestTimeoutMs): Promise<DownloadInfo | null> {
  return new Promise<DownloadInfo | null>(resolve => {
    let settled = false
    const finish = (value: DownloadInfo | null) => {
      if (settled) return
      settled = true
      resolve(value)
    }
    const timer = window.setTimeout(() => finish(null), timeoutMs)
    try {
      fetch(DownloadInfoUrl, { method: 'GET', credentials: 'same-origin' })
        .then(response => (response.ok ? response.json() : null))
        .then((envelope: { code?: unknown; data?: unknown } | null) => {
          window.clearTimeout(timer)
          finish(envelope && envelope.code === 200 ? toDownloadInfo(envelope.data) : null)
        })
        .catch(() => {
          window.clearTimeout(timer)
          finish(null)
        })
    } catch {
      // 极老内核上 fetch 本身缺失或同步抛错（polyfill 没装上）：同样静默。
      window.clearTimeout(timer)
      finish(null)
    }
  })
}
